import React, { useState, useRef } from "react";
import Papa from "papaparse";
import axios from "axios";
import BASE_URL from "../../../assets/global/url";
import { Button, Form } from "react-bootstrap";
import swal from "sweetalert";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";

// Type "Retained Earnings": creates a posted cutoff, a Retained Net Income and
// an Approved Other Income with the Amount.
// Type "Profit Distribution": creates a Distributed Profit Distribution linked
// to the Retained Net Income whose cutoff covers "Month Cover To", with Debit
// and Credit journal lines of the Amount.
// The first rows of the template are samples and are skipped on upload.
const REQUIRED_COLUMNS = [
  "Name",
  "Month Cover From",
  "Month Cover To",
  "Amount",
  "Type",
];
const SAMPLE_ROWS = [
  'Retained Earnings 2021-2024,01/01/2021,12/31/2024,"20,053,818.29",Retained Earnings',
  'Retained Earnings 2025,01/01/2025,12/31/2025,"31,530,541.62",Retained Earnings',
  'Retained Earnings 2026,01/01/2026,08/31/2026,"16,730,236.79",Retained Earnings',
  'Profit Distribution 2025,01/01/2025,12/31/2025,"15,499,762.14",Profit Distribution',
];

const RetainedEarningsMigration = () => {
  const userLoggedID = useDecodeToken();
  const [csvData, setCsvData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const fileRef = useRef();

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".csv") && file.type !== "text/csv") {
      swal({
        icon: "warning",
        title: "Unsupported File Type",
        text: "Please upload a valid CSV file only.",
      }).then(() => (fileRef.current.value = ""));

      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      encoding: "UTF-8",
      complete: function (results) {
        const missingColumns = REQUIRED_COLUMNS.filter(
          (column) => !results.meta.fields?.includes(column),
        );

        if (missingColumns.length) {
          swal({
            icon: "warning",
            title: "File Format Mismatch",
            text: "Your file doesn't match the expected template for Retained Earnings Migration. Please download the template and try again.",
          }).then(() => (fileRef.current.value = ""));

          return;
        }

        // Skip the sample rows from the template
        const cleanedData = results.data.slice(SAMPLE_ROWS.length);

        if (!cleanedData.length) {
          swal({
            icon: "warning",
            title: "No data found in file",
            text: `Please add your rows after the ${SAMPLE_ROWS.length} sample rows.`,
          }).then(() => (fileRef.current.value = ""));

          return;
        }

        setCsvData(cleanedData);
      },
    });
  };

  const handleSendToServer = async () => {
    if (!fileRef.current?.value) {
      swal({
        icon: "warning",
        title: "No File Selected",
        text: "Please choose a file before proceeding.",
      });
      return;
    }

    setIsLoading(true);

    try {
      const res = await axios.post(
        BASE_URL + "/migration/retained-earnings-data-migrate",
        { data: csvData, userLoggedID },
      );

      if (res.status === 200) {
        swal({
          icon: "success",
          title: "Data Uploaded Successfully!",
          text: res.data.message,
        });
        fileRef.current.value = "";
        setCsvData([]);
      }
    } catch (error) {
      console.error("Upload error:", error);

      if (error.response?.status === 400) {
        swal({
          icon: "warning",
          title: "Unable to Migrate",
          text: error.response.data.message,
        });
        return;
      }

      swal({
        title: "Something went wrong",
        text: "Please contact your support immediately",
        icon: "error",
        timer: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    const csvContent = [REQUIRED_COLUMNS.join(","), ...SAMPLE_ROWS].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Retained Earnings Template.csv");
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="row p-2 mx-auto align-items-end">
        <div className="col-sm-8">
          <Form.Label className="fw-bold">
            Migrate Retained Earnings &amp; Profit Distribution
          </Form.Label>
          <Form.Control
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            ref={fileRef}
          />
          <small className="text-muted">
            Dates are MM/DD/YYYY. Type is "Retained Earnings" or "Profit
            Distribution". The first {SAMPLE_ROWS.length} rows of the template
            are samples and will be skipped; add your data below them.
          </small>
        </div>
        <div className="col-sm-4 mt-2">
          {isLoading ? (
            <p className="mb-2 fw-bold loading">
              Migrating<span className="dots"></span>
            </p>
          ) : (
            <>
              <Button
                variant="warning"
                className="mt-4 mx-1"
                onClick={handleDownload}
              >
                Download Template
              </Button>
              <Button className="mt-4" onClick={handleSendToServer}>
                Upload to Server
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RetainedEarningsMigration;
